import {
  GateDTO,
  GateGuestDTO,
  TimeSlot,
  WeekDay,
} from '@/../client-generator/generated-clients/plants';
import { PropType } from 'vue';
import { Options, Vue } from 'vue-class-component';
import TimesSlot from './TimesSlot/TimesSlot.vue';
import i18n from '@/core/translations/i18n';
import { TimeSlotWithValidation } from '../GuestDetailsEdit';

type SelectButtonValue = {
  label: string;
  value: boolean | WeekDay;
};

@Options({
  name: 'GateAccessTab',
  props: {
    editingGuest: {
      type: Object as PropType<GateGuestDTO>,
      required: true,
    },
    timesSlotsWithValidation: {
      type: Object as PropType<TimeSlotWithValidation>,
      required: true,
    },
    gates: {
      type: Object as PropType<GateDTO[]>,
      required: true,
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled:{
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    'editingGuest.isTimeAlways': function (newValue: boolean) {
      if (!newValue) {
        if (this.timesSlotsWithValidation.length === 0) this.addTimeSlot();
      } else {
        this.timesSlotsWithValidation.splice(
          0,
          this.timesSlotsWithValidation.length
        );
      }
      this.guestWasEdited();
    },

    'editingGuest.isDateAlways': function (newValue: boolean) {
      if (!newValue) {
        this.dateSince = this.noModification.durationSince
          ? new Date(this.noModification.durationSince.toISOString().split('Z')[0])
          : new Date(this.tomorrowDate);
        this.dateUntil = this.noModification.durationUntil
          ? new Date(this.noModification.durationUntil.toISOString().split('Z')[0])
          : new Date(this.tomorrowDate);
      } else {
        this.dateSince = undefined;
        this.dateUntil = undefined;
        this.editingGuest.durationSince = undefined;
        this.editingGuest.durationUntil = undefined;
      }
      this.guestWasEdited();
    },
    'timesSlotsWithValidation.length': function () {
      this.guestWasEdited();
    },
  },
  components: {
    TimesSlot,
  },
  emits: ['gate-access-edited', 'error-gate-access'],
})
export default class GateAccessTab extends Vue {
  editingGuest!: GateGuestDTO;
  gates!: GateDTO[];
  isEdit!: boolean;
  disabled!: boolean;
  noModification = new GateGuestDTO();

  dateSince = new Date();
  dateUntil = new Date();

  dateSettingsList: SelectButtonValue[] = [
    {
      label: i18n.global.t('AppResourceEP.Always'),
      value: true,
    },
    {
      label: i18n.global.t('AppResourceEP.DateRange'),
      value: false,
    },
  ];
  timeSettingsList: SelectButtonValue[] = [
    {
      label: i18n.global.t('AppResourceEP.Always'),
      value: true,
    },
    {
      label: i18n.global.t('AppResourceEP.TimeRange'),
      value: false,
    },
  ];
  weekDaySettingList: SelectButtonValue[] = [
    {
      label: i18n.global.t('AppResource.FirstWeekdayInitial'),
      value: WeekDay.Monday,
    },
    {
      label: i18n.global.t('AppResource.SecondWeekdayInitial'),
      value: WeekDay.Tuesday,
    },
    {
      label: i18n.global.t('AppResource.ThirdWeekdayInitial'),
      value: WeekDay.Wednesday,
    },
    {
      label: i18n.global.t('AppResource.FourthWeekdayInitial'),
      value: WeekDay.Thursday,
    },
    {
      label: i18n.global.t('AppResource.FifthWeekdayInitial'),
      value: WeekDay.Friday,
    },
    {
      label: i18n.global.t('AppResource.SixthWeekdayInitial'),
      value: WeekDay.Saturday,
    },
    {
      label: i18n.global.t('AppResource.SeventhWeekdayInitial'),
      value: WeekDay.Sunday,
    },
  ];

  timesSlotsWithValidation!: TimeSlotWithValidation[];

  get minDate() {
    const today = new Date();
    return today;
  }

  get maxDate() {
    const nextYear = new Date(this.minDate);
    nextYear.setUTCFullYear(this.minDate.getUTCFullYear() + 1);
    return nextYear;
  }

  get endDate() {
    const nextDay = new Date(this.minDate);
    nextDay.setUTCDate(this.minDate.getUTCDate() + 1);
    return nextDay;
  }

  get minDurationUntilDate() {
    const nextDay = new Date(this.editingGuest.durationSince!);
    return nextDay;
  }
  get dateNotValid() {
    if (this.editingGuest.durationSince && this.editingGuest.durationUntil) {
      return this.editingGuest.durationUntil < this.editingGuest.durationSince;
    } else {
      return false;
    }
  }

  get gateAccessTabHasError() {
    const allSlotValid = this.timesSlotsWithValidation.every(
      (slot) => slot.isValid
    );
    return !allSlotValid || this.dateNotValid;
  }

  get tomorrowDate() {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setUTCDate(today.getDate() + 1);
    tomorrow.setUTCHours(0, 0, 0, 0);
    return tomorrow;
  }

  mounted(): void {
    if (!this.editingGuest.isDateAlways) {   
      this.dateSince = new Date(this.editingGuest.durationSince!.toISOString().split('Z')[0]);
      this.dateUntil = new Date(this.editingGuest.durationUntil!.toISOString().split('Z')[0]);
    }

    this.noModification = new GateGuestDTO(this.editingGuest);
  }

  dateSinceUpdate() {
    this.editingGuest.durationSince = this.createDate(this.dateSince);
    this.guestWasEdited();
  }

  dateUntilUpdate() {
    this.editingGuest.durationUntil = this.createDate(this.dateUntil);
    if (this.editingGuest.isOneTimeGuest) {
      this.dateSince = this.dateUntil;
      this.editingGuest.durationSince = this.editingGuest.durationUntil;
    }
    this.guestWasEdited();
  }

  addTimeSlot() {
    if (this.timesSlotsWithValidation.length < 2) {
      const since = new Date();
      since.setUTCHours(0, 0, 0, 0);
      const until = new Date();
      until.setUTCHours(0, 0, 0, 0);
      this.timesSlotsWithValidation.push({
        slot:  new TimeSlot(
          {
            timeSince: since,
            timeUntil: until,
          }
        ),
        isValid: false,
      });
     }
     
  }

  handleTimeSlotRemove(slot: TimeSlotWithValidation) {
    const slotIndex = this.timesSlotsWithValidation.indexOf(slot);
    this.timesSlotsWithValidation.splice(slotIndex, 1);
  }

  guestWasEdited() {
    this.$emit('error-gate-access', this.gateAccessTabHasError);
    this.$emit('gate-access-edited', this.gateAccessEdited());
  }

  gateAccessEdited() {
    const gateIdsEdited =
      this.editingGuest.gateIds?.toString() !==
      this.noModification.gateIds?.toString();

    const timeEdited = this.timeSlotPermissionEdited();

    if (this.editingGuest.isRecurrent) {
      const datePermissionsEdited = this.recurrentGuestEdited();

      return gateIdsEdited || timeEdited || datePermissionsEdited;
    }
    if (this.editingGuest.isOneTimeGuest) {
      const durationUntilEdited =
        this.editingGuest.durationUntil?.toLocaleDateString() !==
          this.noModification.durationUntil?.toLocaleDateString() &&
        !this.dateNotValid;

      return gateIdsEdited || timeEdited || durationUntilEdited;
    }
  }

  timeSlotPermissionEdited() {
    const timeRangeEdited =
      this.editingGuest.isTimeAlways !== this.noModification.isTimeAlways;
    const timesSlotsEdited = this.timesSlotsWithValidation.some((editSlot) => {
      return this.noModification.timesSlots?.every((guestSlot) => {
        return (
          this.noModification.timesSlots?.length !==
            this.timesSlotsWithValidation.length ||
          guestSlot.id !== editSlot.slot.id ||
          guestSlot.timeSince?.getTime() !==
            editSlot.slot.timeSince?.getTime() ||
          guestSlot.timeUntil?.getTime() !==
            editSlot.slot.timeUntil?.getTime()
        );
      });
    });
    const allSlotValid = this.timesSlotsWithValidation.every(
      (slot) => slot.isValid
    );

    return (timeRangeEdited || timesSlotsEdited) && allSlotValid;
  }

  recurrentGuestEdited() {
    const dateRangeEdited =
      this.editingGuest.isDateAlways !== this.noModification.isDateAlways;

    const durationSinceEdited =
      !this.editingGuest.isDateAlways &&
      this.editingGuest.durationSince?.valueOf() !==
        this.noModification.durationSince?.valueOf();

    const durationUntilEdited =
      !this.editingGuest.isDateAlways &&
      this.editingGuest.durationUntil?.valueOf() !==
        this.noModification.durationUntil?.valueOf() &&
      !this.dateNotValid;

    const repetitionDaysEdited =
      this.editingGuest.repetitionDays?.length !==
        this.noModification.repetitionDays?.length ||
      this.editingGuest.repetitionDays?.some(
        (day) => !this.noModification.repetitionDays?.includes(day)
      );

    return (
      dateRangeEdited ||
      durationSinceEdited ||
      durationUntilEdited ||
      repetitionDaysEdited
    );
  }

  createDate(date?: Date,) {
    if(!date) return undefined;
    const newDate = new Date();
    newDate.setUTCDate(date.getDate());
    newDate.setUTCMonth(date.getMonth());
    newDate.setUTCFullYear(date.getFullYear());
    newDate.setUTCHours(0, 0, 0, 0);
    return newDate;
  }
}
